import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Popover, Drawer, Menu, Spin, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useWalletInfo } from '../../../hooks/useWalletInfo';
import { cutString } from '../../../utils';
import {
  CopyOutlined,
  DownOutlined,
  ExportOutlined,
  MenuOutlined,
  LoadingOutlined,
  RightOutlined,
} from '@ant-design/icons';

import cfxIcon from '../../../images/cfx.svg';
import Logo from '../logo';
import NotAllow from '../../../images/not-allow.png';
import useCurrentNetwork from '../../../hooks/useCurrentNetwork';
import "./index.css";
import poolConfig from '../../../../pool.config';

const antIcon = <LoadingOutlined style={{ fontSize: 16 }} spin />;

function Header() {
  const { t } = useTranslation();
  // const { account: address, isAccountLoading } = useAccount();
  // const { isNetworkMatch, isNetworkMatchLoading } = useIsNetworkMatch();

  // const isLoading = isAccountLoading || isNetworkMatchLoading;

  // const networkError = !isLoading && !isNetworkMatch && !!address;
  // const networkError = useMemo(() => {
  //   return !isLoading && !isNetworkMatch && !!address;
  // }, [isLoading, isNetworkMatch, address]);
  const { account: address, networkError, isLoading } = useWalletInfo();

  const { network, isTestNetEnv } = useCurrentNetwork();

  const [drawerVisible, setDrawerVisible] = useState(false);

  const navigate = useNavigate();
  const handleRouter = (url) => {
    if (url.startsWith('https')) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
    setDrawerVisible(false);
  };

  const goExplorer = () => {
    const explorerUrl = isTestNetEnv ? poolConfig.testnet.explorer : poolConfig.mainnet.explorer;
    window.open(`${explorerUrl}${address}`, '_blank');
  };

  const handleCopyAddress = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(address);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = address;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (!copied) {
          throw new Error('Copy command failed');
        }
      }

      message.success('Copied Successfully');
    } catch (error) {
      message.error('Unable to copy address');
    }
  };

  const goDashboard = () => {
    navigate('/conflux/cspace/dashboard');
    setDrawerVisible(false);
  };

  const handleActivate = async () => {
    if (typeof window.conflux === 'undefined') {
      console.error('Fluent Wallet has not been install yet!');
      window.open('https://fluentwallet.com/', '_blank');
      return;
    }

    try {
      await window.conflux.request({ method: 'cfx_requestAccounts' });

    } catch (error) {
      console.error(error);
    }

  };

  const items = [
    { label: 'Conflux', key: 'espace', url: poolConfig.espaceUrl },
    { label: 'Help', key: 'help', url: poolConfig.helpUrl },
    { label: 'Contact', key: 'contact', url: poolConfig.contactUrl },
  ];

  const DrawerContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center mb-8">
        <Logo className="w-8 h-8" />
        <span className="ml-4 text-lg font-bold">Goldshell Stake</span>
      </div>
      {address}
      <div className="flex flex-col space-y-6">
        {items.map((item) => (
          <div
            key={item.key}
            className="text-base font-semibold cursor-pointer"
            onClick={() => handleRouter(item.url)}
          >
            {item.label}
          </div>
        ))}
        <div
          className="text-base font-semibold cursor-pointer"
          onClick={goDashboard}
        >
          Dashboard
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-[#fbfbfb]">
      <nav className="container mx-auto max-w-[1280px] p-4">
        <div className="flex items-center justify-between font-semibold">
          <div className="flex items-center">
            <Logo />
            <a href="/" className="hidden ml-2 text-base font-bold md:block hover:text-black">
              Goldshell Stake
            </a>

            <div className="items-center hidden ml-12 space-x-12 text-base font-semibold md:flex">
              {items.map((data, index) => (
                <div key={index} className="cursor-pointer" onClick={() => handleRouter(data.url)}>
                  {data.label}
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {isLoading ? (
              <button className="btn-outline h-8 inline-flex items-center gap-2 rounded-md py-1.5 px-3 text-sm/6 font-semibold border">
                <Spin indicator={antIcon} />
              </button>
            ) : address ? (
              <Popover
                overlayClassName="account-center-popover"
                placement="bottomRight"
                align={{ offset: [0, -2] }}
                content={
                  <div className="account-center">
                    <div className="account-center-header">
                      <div>
                        <div className="account-center-title">Account</div>
                        <div className="account-center-network">
                          <span className="account-center-status" aria-hidden="true" />
                          Conflux {isTestNetEnv ? 'TESTNET' : 'MAINNET'}
                        </div>
                      </div>
                    </div>

                    <div className="account-center-wallet">
                      <div className="cfx-wallet-avatar" aria-hidden="true">
                        <img src={cfxIcon} alt="" />
                      </div>
                      <div className="account-center-wallet-copy">
                        <div className="account-center-label">Connected wallet</div>
                        <div className="account-center-address" title={address}>
                          {cutString(address, 7, 8)}
                        </div>
                      </div>
                    </div>

                    <div className="account-center-actions">
                      <button
                        type="button"
                        onClick={handleCopyAddress}
                        className="account-center-action"
                      >
                        <span className="account-center-action-icon" aria-hidden="true">
                          <CopyOutlined />
                        </span>
                        <span className="account-center-action-copy">
                          <span className="account-center-action-title">Copy Address</span>
                          <span className="account-center-action-description">Copy to clipboard</span>
                        </span>
                        <RightOutlined className="account-center-action-arrow" aria-hidden="true" />
                      </button>

                      <button
                        type="button"
                        onClick={goExplorer}
                        className="account-center-action"
                      >
                        <span className="account-center-action-icon" aria-hidden="true">
                          <ExportOutlined />
                        </span>
                        <span className="account-center-action-copy">
                          <span className="account-center-action-title">View on Explorer</span>
                          <span className="account-center-action-description">Open account activity</span>
                        </span>
                        <RightOutlined className="account-center-action-arrow" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                }
                trigger="click"
              >
                <button type="button" className="account-center-trigger">
                  <span className="account-center-trigger-avatar" aria-hidden="true" />
                  <span>{cutString(address, 4, 6)}</span>
                  <DownOutlined className="account-center-trigger-chevron" aria-hidden="true" />
                </button>
              </Popover>
            ) : (
              <button
                onClick={handleActivate}
                className="btn-outline h-8 inline-flex items-center gap-2 rounded-md py-1.5 px-3 text-sm/6 font-semibold border focus:outline-none active:shadow-none active:bg-gray-300 active:border-gray-500 transition-all duration-150 ease-in-out"
              >
                {t('Header.connect_fluent')}
              </button>
            )}

            <div className="items-center hidden md:flex">
              <div className="mx-4 text-base text-gray-300">|</div>
              <div className="text-base font-medium cursor-pointer" onClick={goDashboard}>
                Dashboard
              </div>
            </div>

            <div className="md:hidden">
              <MenuOutlined onClick={() => setDrawerVisible(true)} />
            </div>
          </div>
        </div>
      </nav>

      <Drawer
        title={null}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        <Menu mode="vertical">
          <DrawerContent />
        </Menu>
      </Drawer>

      {networkError &&
        createPortal(
          <div className="fixed top-0 left-0 w-[100vw] h-[100vh] bg-black bg-opacity-25 z-50">
            <div className="absolute top-[40%] left-[50%] translate-x-[-50%] translate-y-[-50%] flex flex-col justify-center items-center w-[240px] md:w-[480px] md:h-[390px] md:px-[28px] md:py-[12px] bg-white shadow rounded-md">
              <img
                className="w-[340px] h-[240px]"
                src={NotAllow}
                alt="not allow imgs"
              />
              <p className="md:text-[32px] md:leading-[32px] text-[#333] md:my-[16px] font-bold">
                {t('Header.error')}
              </p>
              <p className="md:text-[16px] md:leading-[24px] text-[#999] text-center md:my-0 font-medium">
                {t('Header.unspport_network_switch', { network: network?.name || '' })}
              </p>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default Header;
